import type { FC, MouseEventHandler, MouseEvent } from 'react';
import { useCallback, useMemo, memo, useRef } from 'react';
import isEqual from 'fast-deep-equal';

import type { ImageProps } from 'antd-mobile/es/components/image';
import { Image } from 'antd-mobile';

import type { MenuListProductItem } from '@/apis/product/types';
import { getCDNAssets } from '@/utils';

import ProductCardTags from './Tags';
import ProductCardPrice from './Price';
import { ProductCardSelectButton, ProductCardCountStepper } from './NumButton';

import { curProductExtraSyncToSession } from '../../../external/trackinfo';

import styles from './styles/card.module.less';

const MyImage = memo(
  (props: ImageProps) => <Image {...props} />,
  (prevProps, nextProps) => prevProps.src === nextProps.src,
);
MyImage.displayName = 'MyImage';

export type ProductCardProps = {
  id?: string;
  orderType?: number;
  item: MenuListProductItem;
  count?: number;
  onClick?: (p: MenuListProductItem, e: MouseEvent<HTMLDivElement>) => void;
  /** 选规格 */
  onSelect?: (p: MenuListProductItem) => void;
  /** add to cart */
  onAdd?: (p: MenuListProductItem) => unknown;
  /** remove */
  onSub?: (p: MenuListProductItem) => unknown;

  // for tracking. ignore
  _extraInfo?: string;
};

const ProductCard: FC<ProductCardProps> = memo(
  (props) => {
    const { id, orderType = 1, count, item, onClick, onSelect, onAdd, onSub, _extraInfo, ...rest } = props;

    const blockClick = useRef(false);
    const onCardClick = useCallback<MouseEventHandler<HTMLDivElement>>(
      (e) => {
        if (!blockClick.current) {
          onClick?.(item, e);

          curProductExtraSyncToSession(_extraInfo);
        }
      },
      [_extraInfo, item, onClick],
    );

    const onCardSelect = useCallback<MouseEventHandler<HTMLDivElement>>(
      (e) => {
        e.stopPropagation();
        onSelect?.(item);

        curProductExtraSyncToSession(_extraInfo);
      },
      [_extraInfo, item, onSelect],
    );
    const onCardAdd = useCallback<MouseEventHandler<HTMLDivElement>>(
      (e) => {
        e.stopPropagation();
        onAdd?.(item);

        curProductExtraSyncToSession(_extraInfo);
      },
      [_extraInfo, item, onAdd],
    );
    const onCardSub = useCallback<MouseEventHandler<HTMLDivElement>>(
      (e) => {
        e.stopPropagation();
        onSub?.(item);

        curProductExtraSyncToSession(_extraInfo);
      },
      [_extraInfo, item, onSub],
    );

    const isMidImgMode = useMemo(() => !!item.productMidImage, [item.productMidImage]);
    const showMemoText = !isMidImgMode && !!item.memo;

    const productTagsNode = useMemo(() => {
      if (item.sellout) {
        return (
          <div className={styles.cardTags} style={{ paddingTop: !showMemoText ? 10 : undefined }}>
            <ProductCardTags tags={[{ name: '当前已售罄', style: 2, color: isMidImgMode ? 'inherit' : '#969696' }]} />
          </div>
        );
      }
      return (
        (item.cardTagImage || (item.tags && item.tags.length > 0)) && (
          <div className={styles.cardTags} style={{ paddingTop: !showMemoText ? 10 : undefined }}>
            {item.cardTagImage ? (
              <div className={styles.cardTagBg}>
                <img src={item.cardTagImage} className={styles.cardTagIcon} />
                <div className={styles.cardTagText}>{item.cardTagText}</div>
              </div>
            ) : (
              item.tags && item.tags.length > 0 && <ProductCardTags tags={item.tags} isSkinTag={isMidImgMode} />
            )}
          </div>
        )
      );
    }, [isMidImgMode, item.cardTagImage, item.cardTagText, item.sellout, item.tags, showMemoText]);

    const productActionNode = useMemo(() => {
      if (item.sellout) {
        return null;
      }

      return item.style?.button ||
        [3].includes(item.productType) ||
        ([2, 7].includes(item.productType) && item.isChoices === 1) ? (
        <ProductCardSelectButton
          style={{
            backgroundImage: item.style?.button?.btnImage ? `url(${item.style.button.btnImage})` : undefined,
            color: item.style?.button?.btnTextColor ? item.style.button.btnTextColor : undefined,
          }}
          count={count}
          onClick={onCardSelect}>
          {item.style?.button?.btnText || '选规格'}
        </ProductCardSelectButton>
      ) : [1, 2, 7].includes(item.productType) ? (
        <ProductCardCountStepper
          showSub={(c) => Boolean(c && c > 0)}
          minCount={0}
          maxCount={item.maxPurchaseQuantity}
          count={count}
          onAdd={onCardAdd}
          onSub={onCardSub}
        />
      ) : null;
    }, [
      count,
      item.isChoices,
      item.maxPurchaseQuantity,
      item.productType,
      item.sellout,
      item.style?.button,
      onCardAdd,
      onCardSelect,
      onCardSub,
    ]);

    return (
      <div
        id={id}
        className={styles.productItem}
        style={{ backgroundImage: isMidImgMode && item.productMidImage ? `url(${item.productMidImage})` : undefined }}
        data-midmode={isMidImgMode}
        data-displaymode={item.style?.displayMode}
        data-sellout={item.sellout}
        onClick={onCardClick}
        onTouchStart={() => {
          blockClick.current = true;
        }}
        onTouchEnd={() => {
          blockClick.current = false;
        }}
        {...rest}>
        {/* TODO delete */}
        {process.env.NODE_ENV === 'development' && (
          <div
            style={{ position: 'absolute', left: 0, top: 0, padding: 2, fontSize: '12px', backgroundColor: '#eeeeee' }}>
            {item.productPromotions?.length > 0 ? (
              <span
                style={{
                  color: 'white',
                  background: 'red',
                  borderRadius: 2,
                  padding: '0 2px',
                  marginRight: 6,
                  fontSize: '10px',
                }}>
                促
              </span>
            ) : null}
            {item.productType} | {item.productCode}
            {item.sellout && ` | sellout`}
          </div>
        )}
        <div className={styles.productItemImg}>
          {!isMidImgMode && (
            <MyImage
              src={item.productImage}
              fit="scale-down"
              fallback={<img className={styles.productItemImg} src={getCDNAssets('menu_list_default_img.png')} />}
              placeholder={<img className={styles.productItemImg} src={getCDNAssets('menu_list_default_img.png')} />}
              lazy
            />
          )}
        </div>
        <div className={styles.productItemContent}>
          <div>
            <div className={styles.productItemContentTitle} style={{ color: item.style?.titleColor || undefined }}>
              {item.productName}
            </div>
            {showMemoText && <div className={styles.productItemContentDesc}>{item.memo}</div>}
            {productTagsNode}
          </div>
          <div className={styles.productItemContentMore}>
            <ProductCardPrice
              style={{ color: item.style?.priceColor || undefined }}
              separateStyle={{ color: item.style?.separatePriceColor || undefined }}
              price={orderType === 2 ? item.priceInfo?.deliveryPriceText : item.priceInfo?.eatInPriceText}
              separatePrice={item.priceInfo?.separatePriceText}
              priceShowStyle={item.priceInfo?.priceShowStyle}
            />
            {productActionNode}
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (isEqual(prevProps.item, nextProps.item) && isEqual(prevProps.count, nextProps.count)) {
      return true;
    }
    return false;
  },
);
ProductCard.displayName = 'ProductCard';

export default ProductCard;
